// @ts-check

/**
 * The mount-ready contract: a registered `sp-*` instance carries
 * `data-sp-ce-ready` and has dispatched `sp:ce:ready` if and only if its
 * exposed methods are callable. Terminal load failures mark the instance
 * `data-sp-ce-failed` and dispatch `sp:ce:failed` instead (see
 * framework/loader.js) — an instance never carries both. Unmounting removes
 * both markers ({@link withMarkerCleanupHook}), so a detached host stops
 * reading as settled and re-earns its marker at remount.
 *
 * The contract is applied at the definition level, in the shared def
 * preparation (framework/component.js, framework/app.js), so it holds under
 * both the eager and the lazy build.
 *
 * Consumers await it through {@link whenMountReady} (window-exposed as
 * `window.Sparkle.CustomElements.whenMountReady` — see
 * entrypoints/custom_elements.js), never through `customElements.whenDefined`:
 * under boot-time definitions `whenDefined` resolves long before the
 * component module loads, so it stopped meaning "operational".
 */

export const MOUNT_READY_MARKER = "data-sp-ce-ready";
export const MOUNT_READY_EVENT = "sp:ce:ready";
export const LOAD_FAILED_MARKER = "data-sp-ce-failed";
export const LOAD_FAILED_EVENT = "sp:ce:failed";

/**
 * Declared on every prepared definition: `VueElement`'s attribute observer
 * echoes host attributes into props, and an undeclared marker would fall
 * through `$attrs` and render as a junk attribute inside the shadow DOM.
 * Declaring the markers as unused props keeps the echo out of `$attrs` — but
 * does NOT suppress the observer-driven update itself (see the cost note on
 * {@link withMountReadyHook}).
 */
export const MARKER_PROPS = Object.freeze({
  dataSpCeReady: { type: String, default: undefined },
  dataSpCeFailed: { type: String, default: undefined },
});

/** First-mount performance marks, one per tag (see framework/loader.js marks). */
const markedTags = new Set();

/**
 * Resolves the custom-element host from a component instance mounted inside a
 * shadow root. Null for non-CE mounts (e.g. SFC-mounted internals in tests).
 *
 * Realm-neutral: an element adopted from another realm carries a foreign
 * ShadowRoot.
 *
 * @param {{ $el?: Element }} vm
 * @returns {HTMLElement | null}
 */
export function hostOf(vm) {
  const root = vm.$el?.getRootNode?.();

  if (!root || root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
    return null;
  }

  // Only a ShadowRoot among document fragments carries a host.
  const host = /** @type {ShadowRoot} */ (root).host;
  return host ? /** @type {HTMLElement} */ (host) : null;
}

/**
 * Merges the mount-ready hook into a definition's `mounted` — by array-merge,
 * never assignment, so a component's own hook is preserved.
 *
 * The marker is set in a post-mount microtask: `VueElement._mount` defines
 * the exposed methods on the host only after `app.mount()` returns, so a
 * synchronous marker would fire before `.open()`-style methods exist. Paint
 * waits for the microtask checkpoint, so the deferral is invisible.
 *
 * Measured cost: the write is not inert — VueElement's blanket attribute
 * observer turns it into one no-op re-render per instance, both modes
 * (noise-level in a 150-instance Chromium probe). The attribute exists
 * because ready/failed state must be CSS-selectable, which an event or JS
 * property cannot provide.
 *
 * @param {import("vue").DefineComponent} component
 * @returns {Array<() => void>}
 */
export function withMountReadyHook(component) {
  const marker = /** @this {{ $el?: Element }} */ function () {
    const host = hostOf(this);

    if (!host) {
      return;
    }

    queueMicrotask(() => {
      if (!markedTags.has(host.localName)) {
        markedTags.add(host.localName);
        performance.mark(`sp-ce:mount:${host.localName}`);
      }

      host.setAttribute(MOUNT_READY_MARKER, "");
      host.dispatchEvent(new CustomEvent(MOUNT_READY_EVENT));
    });
  };

  const existing = component.mounted;
  return existing ? [].concat(existing, marker) : [marker];
}

/**
 * Merges the marker-cleanup hook into a definition's `beforeUnmount` — by
 * array-merge, never assignment, so a component's own hook is preserved.
 * Clearing at unmount keeps a marker meaning a live, callable instance: a
 * truly unmounted host no longer reads as settled, while a host whose
 * deferred unmount cancels keeps its marker (the hook never fires).
 *
 * @param {import("vue").DefineComponent} component
 * @returns {Array<() => void>}
 */
export function withMarkerCleanupHook(component) {
  const cleanup = /** @this {{ $el?: Element }} */ function () {
    const host = hostOf(this);

    if (!host) {
      return;
    }

    host.removeAttribute(MOUNT_READY_MARKER);
    host.removeAttribute(LOAD_FAILED_MARKER);
  };

  const existing = component.beforeUnmount;
  return existing ? [].concat(existing, cleanup) : [cleanup];
}

/**
 * Awaits an instance's mount-ready contract, TOCTOU-safe: if a settlement
 * marker is already present it settles immediately, otherwise it listens for
 * whichever settlement event fires first (both markers are set before their
 * event dispatches, so no settlement can fall between the check and the
 * listen). Resolves with the element once its exposed methods are callable;
 * rejects when the instance settled as a terminal load failure — the error
 * branch that keeps waiters from deadlocking on a failed tag.
 *
 * Under a config failure every started catalog instance settles as a
 * terminal `config-error` load failure, so this promise rejects — waiters
 * wake instead of deadlocking on a page that will never become operational.
 *
 * @param {Element} element - an instance of a registered catalog tag.
 * @returns {Promise<Element>} rejects with a TypeError for a non-element
 *   argument — the window-exposed helper is commonly fed a raw
 *   `querySelector` result, and a clear message beats the bare TypeError a
 *   null would otherwise produce.
 */
export function whenMountReady(element) {
  return new Promise((resolve, reject) => {
    // Duck-typed rather than `instanceof Element`, which is realm-sensitive
    // (an element from another window/iframe has a different constructor).
    if (!element || typeof element.hasAttribute !== "function") {
      reject(
        new TypeError(`whenMountReady expects an element, received ${element === null ? "null" : typeof element}`),
      );
      return;
    }

    if (element.hasAttribute(MOUNT_READY_MARKER)) {
      resolve(element);
      return;
    }

    if (element.hasAttribute(LOAD_FAILED_MARKER)) {
      reject(loadFailedError(element));
      return;
    }

    const onReady = () => {
      element.removeEventListener(LOAD_FAILED_EVENT, onFailed);
      resolve(element);
    };
    const onFailed = () => {
      element.removeEventListener(MOUNT_READY_EVENT, onReady);
      reject(loadFailedError(element));
    };

    element.addEventListener(MOUNT_READY_EVENT, onReady, { once: true });
    element.addEventListener(LOAD_FAILED_EVENT, onFailed, { once: true });
  });
}

/**
 * @param {Element} element
 * @returns {Error}
 */
function loadFailedError(element) {
  const reason = element.getAttribute(LOAD_FAILED_MARKER) || "unknown";
  return new Error(`<${element.localName}> failed to load (${reason})`);
}
